import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, Check, Sliders } from "lucide-react";

const STORAGE_KEY = "kb_consent_v1";
const HIDDEN_ROUTES = ["/login", "/register"];

export function getConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
  catch { return null; }
}

export default function CookieConsentBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ functional: true, analytics: false, marketing: false });

  useEffect(() => {
    const stored = getConsent();
    if (!stored) setVisible(true);
  }, []);

  const persist = (state) => {
    const data = { ...state, ts: new Date().toISOString(), version: 1 };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setVisible(false);
    // Tracker dürfen erst NACH dieser Funktion feuern; aktuell keine externen Tracker eingebunden
    window.dispatchEvent(new CustomEvent("kb-consent-updated", { detail: data }));
  };

  if (!visible) return null;
  if (HIDDEN_ROUTES.includes(location.pathname)) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 pointer-events-none" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto pointer-events-auto rounded-lg border border-zinc-800 bg-zinc-950/95 backdrop-blur-md shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-md bg-brand/20 flex items-center justify-center shrink-0">
            <Cookie className="h-5 w-5 text-brand-lighter" />
          </div>
          <div>
            <div className="font-semibold text-zinc-100 text-sm sm:text-base">Cookies &amp; Datenschutz</div>
            <p className="text-xs sm:text-sm text-zinc-400 mt-1 leading-relaxed">
              Wir verwenden essenzielle Cookies zur Anmeldung. Mit deiner Zustimmung nutzen wir zusätzlich Analyse- und Marketing-Cookies, um KickBalance zu verbessern.
              Details in unserer{" "}
              <a href="/?legal=datenschutz" className="text-brand-lighter underline">Datenschutzerklärung</a>{" "}
              und im{" "}
              <a href="/?legal=impressum" className="text-brand-lighter underline">Impressum</a>.
            </p>
          </div>
        </div>

        {showSettings && (
          <div className="mt-4 space-y-2 rounded-md border border-zinc-800 bg-zinc-900/60 p-3" data-testid="cookie-settings">
            <PrefRow label="Funktional (zwingend)" desc="Anmeldung, Mannschaftswahl, Sicherheit"
              checked disabled />
            <PrefRow label="Analyse" desc="Anonyme Nutzung, Fehler-Tracking"
              checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testId="cookie-pref-analytics" />
            <PrefRow label="Marketing" desc="Personalisierte Empfehlungen, externe Pixel"
              checked={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              testId="cookie-pref-marketing" />
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button onClick={() => persist({ functional: true, analytics: false, marketing: false })}
            variant="outline" className="bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-200 rounded-md h-10"
            data-testid="cookie-reject-all">
            Alle ablehnen
          </Button>
          <Button onClick={() => setShowSettings((v) => !v)}
            variant="outline" className="bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-200 rounded-md h-10"
            data-testid="cookie-settings-toggle">
            <Sliders className="h-3.5 w-3.5 mr-2" /> Einstellungen
          </Button>
          <Button onClick={() => persist(showSettings ? { ...prefs, functional: true } : { functional: true, analytics: true, marketing: true })}
            className="bg-brand hover:bg-brand-light text-white rounded-md h-10 font-semibold"
            data-testid="cookie-accept-all">
            <Check className="h-4 w-4 mr-2" /> {showSettings ? "Auswahl speichern" : "Alle akzeptieren"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, desc, checked, onChange, disabled, testId }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={!!checked} disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 accent-brand" data-testid={testId} />
      <div className="flex-1">
        <div className="text-sm text-zinc-100">{label}</div>
        <div className="text-[11px] text-zinc-500">{desc}</div>
      </div>
    </label>
  );
}
